import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Plus, Globe, Edit, Trash2, X, Loader2, Copy, Check, CheckCircle2, Clock,
    AlertTriangle, Archive, Search, FileText, Eye, ArrowRight, Send, Image,
    Tag, Link2, DollarSign, ClipboardCopy, ChevronDown
} from 'lucide-react';
import { PlatformIcon } from '../../components/PlatformIcon';
import { toast } from 'sonner';
import { API } from '../../config/apiBase';

const STATUS_MAP = {
    draft: { label: 'Rascunho', color: '#6b7280' },
    review: { label: 'Em revisao', color: '#f59e0b' },
    ready: { label: 'Pronto', color: '#3b82f6' },
    published: { label: 'Publicado', color: '#22c55e' },
    rejected: { label: 'Rejeitado', color: '#ef4444' },
    archived: { label: 'Arquivado', color: '#6b7280' },
};
const STATUS_FLOW = ['draft', 'review', 'ready', 'published'];

export default function CommunityPublishPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const tab = searchParams.get('tab') || 'publicacoes';
    const setTab = (t) => setSearchParams({ tab: t });

    const [platforms, setPlatforms] = useState([]);
    const [pubs, setPubs] = useState([]);
    const [deals, setDeals] = useState([]);
    const [loading, setLoading] = useState(true);
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
    const authH = { 'Authorization': `Bearer ${token}` };

    const fetchPlatforms = useCallback(async () => {
        try { const r = await fetch(`${API}/community-platforms`, { headers: authH }); if (r.ok) setPlatforms(await r.json()); } catch {}
    }, []);
    const fetchPubs = useCallback(async () => {
        try { const r = await fetch(`${API}/community-publications`, { headers: authH }); if (r.ok) setPubs(await r.json()); } catch {} finally { setLoading(false); }
    }, []);
    const fetchDeals = useCallback(async () => {
        try { const r = await fetch(`${API}/produtos?limit=50`); if (r.ok) { const d = await r.json(); setDeals(d.deals || []); } } catch {}
    }, []);

    useEffect(() => { fetchPlatforms(); fetchPubs(); fetchDeals(); }, []);

    return (
        <div className="sv-page" data-testid="community-publish-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="text-lg font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Publicar em Comunidades</h2>
                    <p className="text-xs" style={{ color: 'var(--muted)' }}>Prepare e organize ofertas para Promobit, Pelando e mais</p>
                </div>
            </div>
            <div className="sv-tabs" data-testid="community-tabs">
                {[['publicacoes', 'Publicacoes', Send], ['plataformas', 'Plataformas', Globe], ['histórico', 'Histórico', Clock]].map(([k, l, Icon]) => (
                    <button key={k} className={`sv-tab ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)} data-testid={`tab-${k}`}>
                        <Icon size={12} /> {l}
                    </button>
                ))}
            </div>
            {tab === 'plataformas' && <PlatformsTab platforms={platforms} fetch={fetchPlatforms} headers={headers} />}
            {tab === 'publicacoes' && <PublicationsTab platforms={platforms} pubs={pubs} deals={deals} fetchPubs={fetchPubs} headers={headers} authH={authH} loading={loading} />}
            {tab === 'histórico' && <HistoryTab pubs={pubs} />}
        </div>
    );
}

/* ========== PLATFORMS TAB ========== */
function PlatformsTab({ platforms, fetch: fetchPlatforms, headers }) {
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ name: '', description: '', notes: '', profile_name: '', profile_signature: '', template: '', order: 0, active: true, posting_mode: 'manual' });
    const [saving, setSaving] = useState(false);
    const TMPL = "{titulo}\n\nDe R$ {preco_original} por R$ {preco} ({desconto}% off)\nLoja: {loja}\n{cupom}\nLink: {link}\n{observacoes}";
    const MODES = [
        { key: 'manual', label: 'Manual', desc: 'Você prepara e publica manualmente', operational: true },
        { key: 'semi_auto', label: 'Semi-automático', desc: 'Sistema prepara, você revisa e aprova', operational: false },
        { key: 'auto', label: 'Automático', desc: 'Sistema publica sozinho com regras', operational: false },
        { key: 'hybrid', label: 'Hibrido', desc: 'Auto para score alto, manual para o resto', operational: false },
    ];

    const openNew = () => { setEditing(null); setForm({ name: '', description: '', notes: '', profile_name: '', profile_signature: '', template: TMPL, order: 0, active: true, posting_mode: 'manual' }); setShowForm(true); };
    const openEdit = (p) => { setEditing(p); setForm({ name: p.name, description: p.description || '', notes: p.notes || '', profile_name: p.profile_name || '', profile_signature: p.profile_signature || '', template: p.template || TMPL, order: p.order || 0, active: p.active !== false, posting_mode: p.posting_mode || 'manual' }); setShowForm(true); };

    const save = async () => {
        if (!form.name.trim()) { toast.error('Nome obrigatorio'); return; }
        setSaving(true);
        try {
            const url = editing ? `${API}/community-platforms/${editing.platform_id}` : `${API}/community-platforms`;
            const r = await fetch(url, { method: editing ? 'PUT' : 'POST', headers, body: JSON.stringify(form) });
            if (r.ok) { toast.success(editing ? 'Atualizada' : 'Criada'); setShowForm(false); fetchPlatforms(); }
        } catch { toast.error('Erro'); } finally { setSaving(false); }
    };

    const del = async (id) => { if (!window.confirm('Excluir?')) return; await fetch(`${API}/community-platforms/${id}`, { method: 'DELETE', headers }); toast.success('Excluida'); fetchPlatforms(); };

    return (
        <div data-testid="platforms-tab">
            <div className="flex items-center justify-between mb-3">
                <span className="sv-card-label">Plataformas Cadastradas</span>
                <button className="sv-btn-gold-system sv-btn-system" onClick={openNew} data-testid="new-platform-btn"><Plus size={14} /> Nova Plataforma</button>
            </div>
            {platforms.length === 0 ? (
                <div className="sv-card-system"><div className="sv-empty-state"><Globe /><p className="sv-empty-state-title">Nenhuma plataforma</p><p className="sv-empty-state-desc">Cadastre Promobit, Pelando, etc.</p></div></div>
            ) : (
                <div className="flex flex-col gap-2">
                    {platforms.map(p => (
                        <div key={p.platform_id} className="sv-card-system" style={{ padding: '14px 18px' }} data-testid={`plat-${p.platform_id}`}>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: p.active ? 'rgba(34,197,94,0.1)' : 'var(--surface-2)' }}>
                                        <PlatformIcon name={p.name} size={18} />
                                    </div>
                                    <div>
                                        <span className="text-sm font-bold" style={{ color: 'var(--text)' }}>{p.name}</span>
                                        {p.profile_name && <span className="text-xs ml-2 px-2 py-0.5 rounded-full" style={{ background: 'rgba(var(--sv-accent-rgb), 0.1)', color: 'var(--sv-gold-primary)' }}>Perfil: {p.profile_name}</span>}
                                        <span className="text-xs ml-2 px-2 py-0.5 rounded-full" style={{ background: 'var(--surface-2)', color: 'var(--muted)' }}>{{'manual':'Manual','semi_auto':'Semi-auto','auto':'Automático','hybrid':'Hibrido'}[p.posting_mode] || 'Manual'}</span>
                                        <span className="text-[10px] ml-1 px-1.5 py-0.5 rounded-full" style={{ background: p.posting_mode === 'manual' ? 'rgba(34,197,94,0.15)' : 'rgba(234,179,8,0.15)', color: p.posting_mode === 'manual' ? '#22c55e' : '#eab308' }}>{p.posting_mode === 'manual' ? 'Operacional' : 'Configurado'}</span>
                                        {p.description && <p className="text-[10px] mt-0.5" style={{ color: 'var(--muted-2)' }}>{p.description}</p>}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className={`sv-badge ${p.active ? 'sv-badge-success' : 'sv-badge-muted'}`} style={{ fontSize: 9 }}>{p.active ? 'Ativa' : 'Inativa'}</span>
                                    <button className="sv-btn-system" style={{ padding: '4px 6px' }} onClick={() => openEdit(p)}><Edit size={11} /></button>
                                    <button className="sv-btn-system" style={{ padding: '4px 6px', color: '#ef4444' }} onClick={() => del(p.platform_id)}><Trash2 size={11} /></button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {showForm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} onClick={e => { if (e.target === e.currentTarget) setShowForm(false); }}>
                    <div className="sv-card-system w-full max-w-xl max-h-[90vh] overflow-y-auto" data-testid="platform-modal">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-base font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>{editing ? 'Editar' : 'Nova'} Plataforma</h3>
                            <button className="sv-btn-system" style={{ padding: 4 }} onClick={() => setShowForm(false)}><X size={14} /></button>
                        </div>
                        <div className="flex flex-col gap-3">
                            <Field label="Nome da plataforma" value={form.name} onChange={v => setForm({ ...form, name: v })} placeholder="Ex: Promobit" testid="plat-name" />
                            <Field label="Descrição interna" value={form.description} onChange={v => setForm({ ...form, description: v })} placeholder="Para que serve essa plataforma" testid="plat-desc" />
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Perfil operacional" value={form.profile_name} onChange={v => setForm({ ...form, profile_name: v })} placeholder="Nome que aparece na plataforma" testid="plat-profile" />
                                <Field label="Assinatura (opcional)" value={form.profile_signature} onChange={v => setForm({ ...form, profile_signature: v })} placeholder="Texto no final do post" testid="plat-sig" />
                            </div>
                            <div>
                                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Template de publicação</label>
                                <textarea value={form.template} onChange={e => setForm({ ...form, template: e.target.value })} rows={5}
                                    className="w-full px-3 py-2.5 rounded-lg text-sm font-mono resize-none" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="plat-template" />
                                <p className="text-[10px] mt-1" style={{ color: 'var(--muted-2)' }}>Variaveis: {'{titulo} {preco} {preco_original} {desconto} {cupom} {loja} {link} {observacoes}'}</p>
                            </div>
                            <Field label="Observações" value={form.notes} onChange={v => setForm({ ...form, notes: v })} placeholder="Notas internas" testid="plat-notes" />
                            <div>
                                <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--muted)' }}>Modo de Postagem</label>
                                <div className="grid grid-cols-2 gap-2">
                                    {MODES.map(m => (
                                        <button key={m.key} onClick={() => setForm({ ...form, posting_mode: m.key })}
                                            className="text-left p-3 rounded-lg transition-all" data-testid={`mode-${m.key}`}
                                            style={{ background: form.posting_mode === m.key ? 'rgba(var(--sv-accent-rgb), 0.12)' : 'var(--surface-2)', border: `1px solid ${form.posting_mode === m.key ? 'var(--sv-gold-primary)' : 'var(--border)'}` }}>
                                            <span className="text-xs font-bold block" style={{ color: form.posting_mode === m.key ? 'var(--sv-gold-primary)' : 'var(--text)' }}>{m.label}</span>
                                            <span className="text-[10px] block mt-0.5" style={{ color: 'var(--muted-2)' }}>{m.desc}</span>
                                            <span className="text-[9px] block mt-1 font-medium" style={{ color: m.operational ? '#22c55e' : '#eab308' }}>{m.operational ? 'Operacional' : 'Aguardando integracao'}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-5">
                            <button className="sv-btn-system" onClick={() => setShowForm(false)}>Cancelar</button>
                            <button className="sv-btn-gold-system sv-btn-system" onClick={save} disabled={saving} data-testid="plat-save-btn">
                                {saving && <Loader2 size={14} className="animate-spin" />} {editing ? 'Salvar' : 'Criar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ========== PUBLICATIONS TAB ========== */
function PublicationsTab({ platforms, pubs, deals, fetchPubs, headers, authH, loading }) {
    const [showPrepare, setShowPrepare] = useState(false);
    const [showEdit, setShowEdit] = useState(null);
    const [selDeal, setSelDeal] = useState('');
    const [selPlat, setSelPlat] = useState('');
    const [prepNotes, setPrepNotes] = useState('');
    const [preparing, setPreparing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [filterStatus, setFilterStatus] = useState('all');
    const [searchText, setSearchText] = useState('');

    const activePlatforms = platforms.filter(p => p.active !== false);

    const prepare = async () => {
        if (!selDeal || !selPlat) { toast.error('Selecione oferta e plataforma'); return; }
        setPreparing(true);
        try {
            const r = await fetch(`${API}/community-publications/prepare`, { method: 'POST', headers, body: JSON.stringify({ deal_id: selDeal, platform_id: selPlat, notes: prepNotes }) });
            if (r.ok) { toast.success('Publicação preparada'); setShowPrepare(false); setSelDeal(''); setSelPlat(''); setPrepNotes(''); fetchPubs(); }
            else { const e = await r.json(); toast.error(e.detail || 'Erro'); }
        } catch { toast.error('Erro'); } finally { setPreparing(false); }
    };

    const updatePub = async (pub_id, data) => {
        setSaving(true);
        try {
            const r = await fetch(`${API}/community-publications/${pub_id}`, { method: 'PUT', headers, body: JSON.stringify(data) });
            if (r.ok) { toast.success('Atualizado'); fetchPubs(); if (showEdit?.pub_id === pub_id) { const d = await r.json(); setShowEdit(d); } }
        } catch { toast.error('Erro'); } finally { setSaving(false); }
    };

    const delPub = async (id) => { if (!window.confirm('Excluir?')) return; await fetch(`${API}/community-publications/${id}`, { method: 'DELETE', headers }); toast.success('Excluido'); setShowEdit(null); fetchPubs(); };

    const copyText = (text) => { navigator.clipboard.writeText(text); toast.success('Texto copiado!'); };

    const filtered = pubs.filter(p => {
        if (filterStatus !== 'all' && p.status !== filterStatus) return false;
        if (searchText && !(p.deal_title || '').toLowerCase().includes(searchText.toLowerCase()) && !(p.platform_name || '').toLowerCase().includes(searchText.toLowerCase())) return false;
        return true;
    });

    return (
        <div data-testid="publications-tab">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex gap-2 flex-wrap">
                    {[['all', 'Todos'], ...Object.entries(STATUS_MAP).map(([k, v]) => [k, v.label])].map(([k, l]) => (
                        <button key={k} className={`sv-subnav-chip ${filterStatus === k ? 'active' : ''}`} onClick={() => setFilterStatus(k)}>{l}</button>
                    ))}
                </div>
                <button className="sv-btn-gold-system sv-btn-system" onClick={() => setShowPrepare(true)} data-testid="prepare-btn"><Plus size={14} /> Preparar Publicação</button>
            </div>
            <div className="sv-card-system mb-3">
                <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-2)' }} />
                    <input type="text" placeholder="Buscar por oferta ou plataforma..." value={searchText} onChange={e => setSearchText(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="pub-search" />
                </div>
            </div>
            {loading ? (
                <div className="sv-card-system"><div className="sv-empty-state"><Loader2 className="animate-spin" /><p className="sv-empty-state-title">Carregando...</p></div></div>
            ) : filtered.length === 0 ? (
                <div className="sv-card-system"><div className="sv-empty-state"><Send /><p className="sv-empty-state-title">Nenhuma publicação</p><p className="sv-empty-state-desc">Prepare ofertas para comunidades</p></div></div>
            ) : (
                <div className="flex flex-col gap-2">
                    {filtered.map(p => {
                        const st = STATUS_MAP[p.status] || STATUS_MAP.draft;
                        return (
                            <div key={p.pub_id} className="sv-card-system cursor-pointer transition-all hover:scale-[1.005]" style={{ padding: '12px 16px' }} onClick={() => setShowEdit(p)} data-testid={`pub-${p.pub_id}`}>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3 min-w-0">
                                        {p.image_url ? <img src={p.image_url} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" /> : <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'var(--surface-2)' }}><FileText size={16} style={{ color: 'var(--muted-2)' }} /></div>}
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>{p.deal_title || p.generated_title}</p>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'var(--surface-2)', color: 'var(--muted)' }}>{p.platform_name}</span>
                                                {p.profile_name && <span className="text-[10px]" style={{ color: 'var(--muted-2)' }}>por {p.profile_name}</span>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        {p.price > 0 && <span className="text-sm font-bold" style={{ color: 'var(--sv-gold-primary)' }}>R$ {p.price?.toFixed(2)}</span>}
                                        <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: `${st.color}18`, color: st.color, border: `1px solid ${st.color}30` }}>{st.label}</span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* PREPARE MODAL */}
            {showPrepare && (
                <Modal onClose={() => setShowPrepare(false)} title="Preparar Publicação" testid="prepare-modal">
                    <div className="flex flex-col gap-3">
                        <div>
                            <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Selecionar Oferta</label>
                            <select value={selDeal} onChange={e => setSelDeal(e.target.value)} className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="select-deal">
                                <option value="">-- Escolha uma oferta --</option>
                                {deals.map(d => <option key={d.deal_id} value={d.deal_id}>{d.title} - R$ {d.current_price?.toFixed(2)} ({d.store_name})</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Plataforma Destino</label>
                            {activePlatforms.length === 0 ? <p className="text-xs" style={{ color: '#f59e0b' }}>Cadastre plataformas na aba "Plataformas" primeiro</p> : (
                                <div className="flex gap-2 flex-wrap">
                                    {activePlatforms.map(p => <button key={p.platform_id} className={`sv-subnav-chip ${selPlat === p.platform_id ? 'active' : ''}`} onClick={() => setSelPlat(p.platform_id)}>{p.name}</button>)}
                                </div>
                            )}
                        </div>
                        <Field label="Observações (opcional)" value={prepNotes} onChange={setPrepNotes} placeholder="Notas para esta publicação" testid="prep-notes" />
                    </div>
                    <div className="flex justify-end gap-2 mt-5">
                        <button className="sv-btn-system" onClick={() => setShowPrepare(false)}>Cancelar</button>
                        <button className="sv-btn-gold-system sv-btn-system" onClick={prepare} disabled={preparing} data-testid="confirm-prepare"><Send size={14} /> Preparar</button>
                    </div>
                </Modal>
            )}

            {/* EDIT/REVIEW MODAL */}
            {showEdit && (
                <Modal onClose={() => setShowEdit(null)} title={`${showEdit.platform_name} - ${showEdit.deal_title}`} testid="edit-pub-modal" wide>
                    <PubDetail pub={showEdit} updatePub={updatePub} delPub={delPub} copyText={copyText} saving={saving} />
                </Modal>
            )}
        </div>
    );
}

/* ========== PUB DETAIL (inside modal) ========== */
function PubDetail({ pub, updatePub, delPub, copyText, saving }) {
    const [title, setTitle] = useState(pub.generated_title || '');
    const [text, setText] = useState(pub.generated_text || '');
    const [notes, setNotes] = useState(pub.notes || '');
    const st = STATUS_MAP[pub.status] || STATUS_MAP.draft;
    const cl = pub.checklist || {};

    const checkItems = [
        { key: 'titulo', label: 'Titulo preenchido', ok: cl.titulo },
        { key: 'preco', label: 'Preco preenchido', ok: cl.preco },
        { key: 'link', label: 'Link preenchido', ok: cl.link },
        { key: 'imagem', label: 'Imagem disponível', ok: cl.imagem },
        { key: 'cupom', label: 'Cupom', ok: cl.cupom, optional: true },
        { key: 'plataforma', label: 'Plataforma selecionada', ok: cl.plataforma },
        { key: 'perfil', label: 'Perfil operacional', ok: cl.perfil },
        { key: 'revisado', label: 'Texto revisado', ok: cl.revisado },
    ];

    const markReviewed = () => {
        const updated = { ...cl, revisado: true };
        updatePub(pub.pub_id, { checklist: updated, generated_title: title, generated_text: text, notes });
    };

    const changeStatus = (s) => updatePub(pub.pub_id, { status: s });
    const saveEdits = () => updatePub(pub.pub_id, { generated_title: title, generated_text: text, notes });

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3 flex-wrap">
                <span className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ background: `${st.color}18`, color: st.color, border: `1px solid ${st.color}30` }}>{st.label}</span>
                {pub.profile_name && <span className="text-xs" style={{ color: 'var(--muted)' }}>Perfil: <strong>{pub.profile_name}</strong></span>}
                {pub.store_name && <span className="text-xs" style={{ color: 'var(--muted)' }}>Loja: {pub.store_name}</span>}
                {pub.price > 0 && <span className="text-xs font-bold" style={{ color: 'var(--sv-gold-primary)' }}>R$ {pub.price?.toFixed(2)}</span>}
                {pub.coupon_code && <span className="text-xs px-2 py-0.5 rounded" style={{ background: 'rgba(34,197,94,0.1)', color: '#22c55e' }}>Cupom: {pub.coupon_code}</span>}
            </div>

            {/* Checklist */}
            <div className="p-3 rounded-xl" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }} data-testid="pub-checklist">
                <p className="text-xs font-bold mb-2" style={{ color: 'var(--text)' }}>Checklist de publicação</p>
                <div className="grid grid-cols-2 gap-1.5">
                    {checkItems.map(c => (
                        <div key={c.key} className="flex items-center gap-1.5">
                            {c.ok ? <CheckCircle2 size={12} style={{ color: '#22c55e' }} /> : c.optional ? <AlertTriangle size={12} style={{ color: '#f59e0b' }} /> : <X size={12} style={{ color: '#ef4444' }} />}
                            <span className="text-[11px]" style={{ color: c.ok ? '#22c55e' : c.optional ? '#f59e0b' : 'var(--muted)' }}>{c.label}{c.optional && !c.ok ? ' (ausente)' : ''}</span>
                        </div>
                    ))}
                </div>
            </div>

            {pub.image_url && <img src={pub.image_url} alt="" className="w-full max-h-48 object-contain rounded-xl" style={{ background: 'var(--surface-2)' }} />}

            {/* Editable fields */}
            <Field label="Titulo" value={title} onChange={setTitle} testid="pub-title" />
            <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Texto da publicação</label>
                <textarea value={text} onChange={e => setText(e.target.value)} rows={6}
                    className="w-full px-3 py-2.5 rounded-lg text-sm font-mono resize-none" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="pub-text" />
            </div>
            <Field label="Observações internas" value={notes} onChange={setNotes} testid="pub-notes" />

            {/* Actions */}
            <div className="flex items-center justify-between flex-wrap gap-2 pt-2" style={{ borderTop: '1px solid var(--border)' }}>
                <div className="flex gap-2 flex-wrap">
                    <button className="sv-btn-system" onClick={() => copyText(text)} data-testid="copy-text-btn"><ClipboardCopy size={12} /> Copiar Texto</button>
                    <button className="sv-btn-system" onClick={markReviewed} data-testid="mark-reviewed-btn"><Check size={12} /> Marcar Revisado</button>
                    <button className="sv-btn-system" onClick={saveEdits} disabled={saving}><Edit size={12} /> Salvar Edicoes</button>
                </div>
                <div className="flex gap-2 flex-wrap">
                    {pub.status === 'draft' && <button className="sv-btn-system" onClick={() => changeStatus('review')} style={{ color: '#f59e0b' }}><Eye size={12} /> Em Revisao</button>}
                    {(pub.status === 'draft' || pub.status === 'review') && <button className="sv-btn-system" onClick={() => changeStatus('ready')} style={{ color: '#3b82f6' }}><Check size={12} /> Pronto</button>}
                    {pub.status === 'ready' && <button className="sv-btn-gold-system sv-btn-system" onClick={() => changeStatus('published')} data-testid="mark-published-btn"><CheckCircle2 size={12} /> Publicado</button>}
                    {pub.status !== 'archived' && <button className="sv-btn-system" onClick={() => changeStatus('archived')} style={{ color: 'var(--muted-2)' }}><Archive size={12} /> Arquivar</button>}
                    <button className="sv-btn-system" style={{ color: '#ef4444' }} onClick={() => delPub(pub.pub_id)}><Trash2 size={12} /></button>
                </div>
            </div>
        </div>
    );
}

/* ========== HISTORY TAB ========== */
function HistoryTab({ pubs }) {
    const published = pubs.filter(p => ['published', 'rejected', 'archived'].includes(p.status));
    const grouped = {};
    published.forEach(p => { const k = p.platform_name || 'Outros'; if (!grouped[k]) grouped[k] = []; grouped[k].push(p); });

    return (
        <div data-testid="history-tab">
            {Object.keys(grouped).length === 0 ? (
                <div className="sv-card-system"><div className="sv-empty-state"><Clock /><p className="sv-empty-state-title">Nenhum histórico</p><p className="sv-empty-state-desc">Publicacoes finalizadas aparecerao aqui</p></div></div>
            ) : (
                Object.entries(grouped).map(([plat, items]) => (
                    <div key={plat} className="sv-card-system mb-3">
                        <div className="sv-card-header"><span className="sv-card-label">{plat}</span><span className="text-xs" style={{ color: 'var(--muted-2)' }}>{items.length} publicacoes</span></div>
                        <div className="flex flex-col gap-1">
                            {items.map(p => {
                                const st = STATUS_MAP[p.status] || STATUS_MAP.draft;
                                return (
                                    <div key={p.pub_id} className="flex items-center justify-between py-2" style={{ borderBottom: '1px solid var(--border)' }}>
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>{p.deal_title}</p>
                                            <div className="flex gap-2 mt-0.5 text-[10px]" style={{ color: 'var(--muted-2)' }}>
                                                {p.profile_name && <span>por {p.profile_name}</span>}
                                                <span>{p.created_at ? new Date(p.created_at).toLocaleDateString('pt-BR') : ''}</span>
                                                {p.notes && <span>- {p.notes.slice(0, 50)}</span>}
                                            </div>
                                        </div>
                                        <span className="text-[10px] px-2 py-0.5 rounded-full shrink-0" style={{ background: `${st.color}18`, color: st.color }}>{st.label}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))
            )}
        </div>
    );
}

/* ========== SHARED COMPONENTS ========== */
function Field({ label, value, onChange, placeholder, testid }) {
    return (
        <div>
            <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>{label}</label>
            <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
                className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid={testid} />
        </div>
    );
}

function Modal({ children, onClose, title, testid, wide }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className={`sv-card-system w-full ${wide ? 'max-w-2xl' : 'max-w-lg'} max-h-[90vh] overflow-y-auto`} data-testid={testid}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-bold truncate pr-4" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>{title}</h3>
                    <button className="sv-btn-system shrink-0" style={{ padding: 4 }} onClick={onClose}><X size={14} /></button>
                </div>
                {children}
            </div>
        </div>
    );
}
